import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Icon } from '@cbrebuild/blocks';
import { uniqueId } from 'lodash';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import LabeledItem from '../../../nucleus/labeled-item/labeled-item';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import RequiredLabeledItem from './RequiredLabeledItem';
import isEnhancedLeaseAbstractionDeal from '../../../utils/lease-abstraction-utils';
import { isValidPostal } from '../voucher-validation';
import { isNumericOutOfRange } from '../../../utils/broker-verification-utils';
import {
  splitPropertyTypeAndSubtype,
  groupPropertyTypesByCategory,
  getPropertyTypes,
  getSubtypesByType,
} from '../../../utils/property-type-utils';
import dealsService from '../../../services/deals-service';
import vouchersService from '../../../services/voucher/vouchers-service';
import states from '../../../data/states.json';

const withLeaseDetailsSection = (url, section) => `${url}?section=${section}`;

// Matches the wording shown in the live voucher flow's AddressForm.js for the same
// non-digit-ZIP condition.
const ZIP_FORMAT_MESSAGE = 'Zip must be 5 digits or ZIP+4';

// Matches LeaseInformationTab.js's own `agreementTypeData`/`transactionTypeData` placeholder --
// re-selecting "Select" commits an empty string, clearing the field.
const SELECT_PLACEHOLDER = { value: '', display_name: 'Select' };

// State dropdown options for the "Before you submit" panel's per-property Address rows --
// `states.json` already stores US entries as `{value: 'CA', display_name: 'California', ...}`,
// matching the `{value, display_name}` shape `RequiredLabeledItem`'s `options` prop expects.
const usStateOptions = states
  .filter(s => s.country_id === 'US')
  .map(({ value, display_name }) => ({ value, display_name }));

// Maps a lease-date `LeaseValidationRule.attribute_key` (backend) to this component's own
// live deal/voucher field value + display label -- used to resolve a `minDateReference`
// (e.g. "occupancy_date must not be before start_date", product-confirmed 2026-07-30) to an
// ACTUAL date/label to compare against. The bounds API only sends the reference metadata
// (`attributeKey`/`operator`) since it has no per-lease context to resolve a live value
// itself (see broker_verification_services.py) -- this component already has every one of
// these 4 fields loaded, so it resolves the comparison client-side. Keys match the same
// explicit `fieldBounds.<key>` mapping used at each `RequiredLabeledItem` call site below.
const buildLeaseDateFieldValues = (deal, voucher) => ({
  sign_date: { value: deal.date_lease_execution, label: 'Lease Execution Date' },
  start_date: { value: deal.date_lease_from, label: 'Lease Start Date' },
  expiration_date: { value: voucher.lease_expiration_date, label: 'Lease Expiration Date' },
  occupancy_date: { value: deal.date_lease_occupancy, label: 'Lease Occupancy Date' },
});

// Merges a resolved live `value`/`label` into `bounds.minDateReference` (consumed by
// `isDateOutOfRange`/`formatOutOfRangeMessage` in broker-verification-utils.js) -- returns
// `bounds` untouched when there's no reference to resolve, or the referenced field doesn't
// have a value yet (nothing to compare against, e.g. Start Date not filled in yet).
const resolveMinDateReference = (bounds, leaseDateFieldValues) => {
  if (!bounds || !bounds.minDateReference) return bounds;
  const referenced = leaseDateFieldValues[bounds.minDateReference.attributeKey];
  if (!referenced || !referenced.value) return bounds;
  return {
    ...bounds,
    minDateReference: { ...bounds.minDateReference, value: referenced.value, label: referenced.label },
  };
};

const PropertyReview = ({ fieldBounds }) => {
  const {
    commercialProperties, deal, voucher, isEnhancedFlow,
  } = useSelector(state => ({
    commercialProperties: state.voucher.commercialProperties,
    deal: state.deal.deal,
    voucher: state.voucher.voucher,
    isEnhancedFlow: isEnhancedLeaseAbstractionDeal(state.deal.deal),
  }));
  const history = useHistory();

  // Dropdown options for the "Before you submit" panel's Rate Type / Lease Transaction Type
  // / Ownership Type / Space Type fields — fetched from the same OPTIONS endpoints the live
  // LeaseInformationTab / PropertySaleTab dropdowns use, so the choices are identical.
  const [agreementTypeOptions, setAgreementTypeOptions] = useState([SELECT_PLACEHOLDER]);
  const [transactionTypeOptions, setTransactionTypeOptions] = useState([SELECT_PLACEHOLDER]);
  const [ownershipTypeOptions, setOwnershipTypeOptions] = useState([]);
  const [spaceTypeOptions, setSpaceTypeOptions] = useState([]);
  const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);

  useEffect(() => {
    dealsService.getDealOptions().then((data) => {
      const agreementChoices = data?.actions?.POST?.lease_agreement_type?.choices || [];
      const transactionChoices = data?.actions?.POST?.lease_income_type?.choices || [];
      // Matches LeaseInformationTab.js's own `agreementTypeData`/`transactionTypeData` --
      // re-selecting "Select" here commits an empty string, clearing the field, same as there.
      if (agreementChoices.length > 0) setAgreementTypeOptions([SELECT_PLACEHOLDER, ...agreementChoices]);
      if (transactionChoices.length > 0) setTransactionTypeOptions([SELECT_PLACEHOLDER, ...transactionChoices]);
    }).catch(() => {});
    vouchersService.getPropertyOptions().then((res) => {
      const voucherCommPropList = res?.actions?.POST || {};
      setOwnershipTypeOptions(voucherCommPropList.ownership_type?.choices || []);
      setSpaceTypeOptions(voucherCommPropList.space_type?.choices || []);
      setPropertyTypeOptions(voucherCommPropList.voucher_property_type_category?.choices || []);
    }).catch(() => {});
  }, []);

  const dealType = deal.deal_type;
  const leaseOrSaleDealType = dealType === 'sale' || dealType === 'lease';
  const emptyState = '--';
  let url;
  let title;
  switch (dealType) {
    case 'lease':
      url = VOUCHER_ROUTES[1].routes[0].path;
      title = 'Lease Information';
      break;
    case 'sale':
      url = VOUCHER_ROUTES[1].routes[3].path;
      title = 'Property';
      break;
    case 'consulting':
    case 'other':
      url = VOUCHER_ROUTES[1].routes[7].path;
      title = 'Overview';
      break;
    default:
  }
  url = url.replace(':id', voucher.deal);
  if (dealType === 'lease') url = withLeaseDetailsSection(url, 'leaseInformation');

  const leaseDateFieldValues = buildLeaseDateFieldValues(deal, voucher);
  const groupedPropertyTypeOptions = groupPropertyTypesByCategory(propertyTypeOptions);

  return (
    <div className="voucher-review-group property-review">
      <div className="navigation">
        <Link className="h2 link" to={url}>{title}</Link>
        <IconButton iconName="edit" onClick={() => { history.push(url); }} />
      </div>
      <div className="content">
        {commercialProperties.length === 0 && <legend className="secondary-legend">There are no properties in this deal.</legend>}
        {dealType === 'lease' &&
          <div>
            <legend className="secondary-legend">Lease Overview</legend>
            <div className="two-cols">
              <div className="col-left">
                <RequiredLabeledItem
                  label="Lease Start Date"
                  item={deal.date_lease_from}
                  notRequired={voucher.is_early_invoice}
                  isReview
                  format="longDate"
                  fieldId="property-lease-start-date"
                  section="Lease Information"
                  controlType="date"
                  editTarget={{ reduxAction: 'updateDeal', fieldPath: 'date_lease_from' }}
                  bounds={resolveMinDateReference(fieldBounds.start_date, leaseDateFieldValues)}
                  rangeFormat="date"
                />
                <RequiredLabeledItem
                  label="Lease Expiration Date"
                  item={voucher.lease_expiration_date}
                  notRequired={voucher.is_early_invoice}
                  isReview
                  format="longDate"
                  fieldId="property-lease-expiration-date"
                  section="Lease Information"
                  controlType="date"
                  editTarget={{ reduxAction: 'updateVoucher', fieldPath: 'lease_expiration_date' }}
                  bounds={resolveMinDateReference(fieldBounds.expiration_date, leaseDateFieldValues)}
                  rangeFormat="date"
                />
                <RequiredLabeledItem
                  label="Lease Execution Date"
                  item={deal.date_lease_execution}
                  notRequired={voucher.is_early_invoice}
                  isReview
                  format="longDate"
                  fieldId="property-lease-execution-date"
                  section="Lease Information"
                  controlType="date"
                  editTarget={{ reduxAction: 'updateDeal', fieldPath: 'date_lease_execution' }}
                  bounds={resolveMinDateReference(fieldBounds.sign_date, leaseDateFieldValues)}
                  rangeFormat="date"
                />
                <RequiredLabeledItem
                  label="Lease Occupancy Date"
                  item={deal.date_lease_occupancy}
                  notRequired={voucher.is_early_invoice}
                  isReview
                  format="longDate"
                  fieldId="property-lease-occupancy-date"
                  section="Lease Information"
                  controlType="date"
                  editTarget={{ reduxAction: 'updateDeal', fieldPath: 'date_lease_occupancy' }}
                  bounds={resolveMinDateReference(fieldBounds.occupancy_date, leaseDateFieldValues)}
                  rangeFormat="date"
                />
              </div>
              <div className="col-right">
                <RequiredLabeledItem
                  label="Rate Type"
                  item={deal.lease_agreement_type}
                  notRequired={voucher.is_early_invoice}
                  isReview
                  fieldId="property-rate-type"
                  section="Lease Information"
                  controlType="select"
                  options={agreementTypeOptions}
                  editTarget={{ reduxAction: 'updateDeal', fieldPath: 'lease_agreement_type' }}
                />
                <RequiredLabeledItem
                  label="Lease Transaction Type"
                  item={deal.lease_income_type}
                  notRequired={voucher.is_early_invoice}
                  isReview
                  fieldId="property-lease-transaction-type"
                  section="Lease Information"
                  controlType="select"
                  options={transactionTypeOptions}
                  editTarget={{ reduxAction: 'updateDeal', fieldPath: 'lease_income_type' }}
                />
                <LabeledItem label="Agile Deal" item={deal.is_agile_deal ? 'Yes' : 'No'} />
                {deal.is_agile_deal &&
                  <div>
                    <LabeledItem label="Number of Seats" item={deal.number_of_seats} emptyState={emptyState} />
                    <LabeledItem label="Cost per Seat" item={deal.cost_per_seat} emptyState={emptyState} />
                  </div>}
                <LabeledItem label="Sublease" item={deal.is_sublease ? 'Yes' : 'No'} />
              </div>
            </div>
          </div>
        }
        {commercialProperties && commercialProperties.filter(p => !isEnhancedFlow || p.location_type_new !== false).map((property, index) => {
          const propertyIdentifier = property.id || index;
          const addressFieldId = `property-${propertyIdentifier}-address`;
          const cityFieldId = `property-${propertyIdentifier}-city`;
          const stateFieldId = `property-${propertyIdentifier}-state`;
          const zipFieldId = `property-${propertyIdentifier}-zip`;
          const propertyLookupFieldIds = {
            address: addressFieldId,
            city: cityFieldId,
            state: stateFieldId,
            zip: zipFieldId,
          };
          const propertyLookupSeed = {
            address: property.address || '',
            city: property.city || '',
            state: property.state_abbreviation || '',
            zip: property.postal_code || '',
          };
          // Non-digit ZIP -- only checked for the plain-text control (see the `controlType`
          // ternary below); lease deals use the combined address-lookup control instead.
          const propertyZipFormatInvalid = dealType !== 'lease'
            && !!property.postal_code && !isValidPostal(property.postal_code, property.country);

          const { type: selectedPropertyType, subtype: selectedPropertySubtype } =
            splitPropertyTypeAndSubtype(property.voucher_property_type_category);

          const propertyTypeSelectOptions = getPropertyTypes(groupedPropertyTypeOptions).map((typeOption) => {
            const defaultFullValue = groupedPropertyTypeOptions[typeOption.value]?.[0]?.value || typeOption.value;
            const { subtype: defaultSubtype } = splitPropertyTypeAndSubtype(defaultFullValue);
            return {
              ...typeOption,
              commitValue: defaultFullValue,
              linkedSubtype: defaultSubtype === 'None' ? '' : defaultSubtype,
            };
          });
          const propertyTypeSelectOptionsWithNone = [
            {
              value: '',
              display_name: 'None',
              commitValue: '',
              linkedSubtype: '',
            },
            ...propertyTypeSelectOptions,
          ];

          const propertySubtypeSelectOptions = getSubtypesByType(
            groupedPropertyTypeOptions,
            selectedPropertyType,
          )
            .filter(opt => opt.value !== 'None')
            .map(opt => ({ ...opt, commitValue: opt.fullValue }));

          return (
            <div key={property.id || index}>
              {(dealType === 'lease' || dealType === 'consulting' || dealType === 'other') &&
                <legend className="secondary-legend">Property #{index + 1}</legend>}
              <div className="two-cols">
                <div className="col-left">
                  {dealType === 'sale' &&
                    <RequiredLabeledItem
                      label="Estimated Close Date"
                      item={property.sale_est_close_date}
                      notRequired={voucher.is_early_invoice}
                      isReview
                      format="longDate"
                      fieldId={`property-${property.id || index}-est-close-date`}
                      section={title}
                      controlType="date"
                      editTarget={{ reduxAction: 'updateCommercialProperty', fieldPath: 'sale_est_close_date', targetId: property.id }}
                    />}
                  <LabeledItem label="Confidential" item={property.is_property_confidential ? ' Yes' : 'No'} />
                  {dealType === 'sale' &&
                    <RequiredLabeledItem
                      label="Ownership Type"
                      item={property.ownership_type}
                      notRequired={voucher.is_early_invoice}
                      isReview
                      fieldId={`property-${property.id || index}-ownership-type`}
                      section={title}
                      controlType="select"
                      options={ownershipTypeOptions}
                      editTarget={{ reduxAction: 'updateCommercialProperty', fieldPath: 'ownership_type', targetId: property.id }}
                    />}
                  <LabeledItem label="Property Name" item={property.name} emptyState={emptyState} />
                  <RequiredLabeledItem
                    label="Address"
                    item={property.address}
                    notRequired={voucher.is_early_invoice}
                    isReview
                    fieldId={addressFieldId}
                    section={title}
                    controlType={dealType === 'lease' ? 'propertyAddressSearch' : 'text'}
                    editTarget={{
                      reduxAction: 'updateCommercialProperty',
                      fieldPath: 'address',
                      targetId: property.id,
                      enablePropertyLookup: dealType === 'lease',
                      propertyLookupField: 'address',
                      propertyLookupFieldIds,
                      propertyLookupSeed,
                    }}
                  />
                  <RequiredLabeledItem
                    label="City"
                    item={property.city}
                    notRequired={voucher.is_early_invoice}
                    isReview
                    fieldId={cityFieldId}
                    section={title}
                    controlType={dealType === 'lease' ? 'propertyAddressSearch' : 'text'}
                    editTarget={{
                      reduxAction: 'updateCommercialProperty',
                      fieldPath: 'city',
                      targetId: property.id,
                      enablePropertyLookup: dealType === 'lease',
                      propertyLookupField: 'city',
                      propertyLookupFieldIds,
                      propertyLookupSeed,
                    }}
                  />
                  <RequiredLabeledItem
                    label="State"
                    item={property.state_abbreviation}
                    notRequired={voucher.is_early_invoice}
                    isReview
                    fieldId={stateFieldId}
                    section={title}
                    controlType="select"
                    options={usStateOptions}
                    editTarget={{ reduxAction: 'updateCommercialProperty', fieldPath: 'state_abbreviation', targetId: property.id }}
                  />
                  <RequiredLabeledItem
                    label="Zip"
                    item={property.postal_code}
                    notRequired={voucher.is_early_invoice}
                    isReview
                    fieldId={zipFieldId}
                    section={title}
                    controlType={dealType === 'lease' ? 'propertyAddressSearch' : 'zip'}
                    // Non-digit ZIP (e.g. "adsda") -- mirrors CommissionsReview.js's
                    // `pursuitIdFormatInvalid`/CompanyContactReview.js's Zip validation. Only
                    // applies to the plain-text `'zip'` control (lease deals use the combined
                    // address-lookup control above, which resolves address/city/state/zip
                    // together from a selected suggestion rather than free digit entry).
                    invalid={propertyZipFormatInvalid}
                    requiredMessage={propertyZipFormatInvalid ? ZIP_FORMAT_MESSAGE : null}
                    editTarget={{
                      reduxAction: 'updateCommercialProperty',
                      fieldPath: 'postal_code',
                      targetId: property.id,
                      enablePropertyLookup: dealType === 'lease',
                      propertyLookupField: 'zip',
                      propertyLookupFieldIds,
                      propertyLookupSeed,
                    }}
                  />
                  {propertyZipFormatInvalid && (
                    <p className="voucher-error-text">{ZIP_FORMAT_MESSAGE}</p>
                  )}
                  <RequiredLabeledItem
                    label="Property Type"
                    item={selectedPropertyType}
                    notRequired={voucher.is_early_invoice}
                    isReview
                    fieldId={`property-${property.id || index}-property-type`}
                    section={title}
                    controlType="select"
                    options={propertyTypeSelectOptionsWithNone}
                    editTarget={{ reduxAction: 'updateCommercialProperty', fieldPath: 'voucher_property_type_category', targetId: property.id }}
                  />
                  <RequiredLabeledItem
                    label="Property Subtype"
                    item={selectedPropertySubtype === 'None' ? '' : selectedPropertySubtype}
                    notRequired={voucher.is_early_invoice || !selectedPropertyType}
                    isReview
                    fieldId={`property-${property.id || index}-property-subtype`}
                    section={title}
                    controlType="select"
                    options={propertySubtypeSelectOptions}
                    editTarget={{ reduxAction: 'updateCommercialProperty', fieldPath: 'voucher_property_type_category', targetId: property.id }}
                  />
                  {leaseOrSaleDealType &&
                    <LabeledItem label="Listed By CBRE" item={property.is_listed ? 'Yes' : 'No'} />}
                  {leaseOrSaleDealType && property.is_listed &&
                    <LabeledItem label="CBRE Listing #" item={property.cbre_listing_number} emptyState={emptyState} />}
                  {leaseOrSaleDealType &&
                    <LabeledItem label="Managed By CBRE" item={property.is_property_managed_by_cbre ? 'Yes' : 'No'} />}
                  <LabeledItem label="Mix Property ID" item={property.mix_property_id} emptyState={emptyState} />
                  {dealType === 'lease' &&
                    <LabeledItem label="Building Class" item={property.building_class} emptyState={emptyState} />}
                  {leaseOrSaleDealType &&
                    <LabeledItem label="Portfolio Name" item={property.portfolio_name} emptyState={emptyState} />}
                </div>
                <div className="col-right">
                  {leaseOrSaleDealType &&
                    <RequiredLabeledItem
                      label="Space Type"
                      item={property.space_type}
                      notRequired={voucher.is_early_invoice && leaseOrSaleDealType}
                      isReview
                      fieldId={`property-${property.id || index}-space-type`}
                      section={title}
                      controlType="select"
                      options={spaceTypeOptions}
                      editTarget={{ reduxAction: 'updateCommercialProperty', fieldPath: 'space_type', targetId: property.id }}
                    />}
                  {dealType === 'sale' &&
                    <LabeledItem label="Number of Tenants" item={property.number_of_tenants} emptyState={emptyState} />}
                  {dealType === 'sale' &&
                    <LabeledItem label="Number of Units" item={property.number_of_units} emptyState={emptyState} />}
                  {property.whole_building && leaseOrSaleDealType &&
                    <RequiredLabeledItem
                      label="Square Footage"
                      item={property.building_total_square_feet}
                      notRequired={voucher.is_early_invoice}
                      isReview
                      fieldId={`property-${property.id || index}-square-footage`}
                      section={title}
                      controlType="number"
                      editTarget={{ reduxAction: 'updateCommercialProperty', fieldPath: 'building_total_square_feet', targetId: property.id }}
                      bounds={fieldBounds.total_leased_area}
                      rangeFormat="number"
                      rangeUnit="Sq. Ft."
                    />}
                  {leaseOrSaleDealType &&
                    <RequiredLabeledItem label="Parcel Size/Acres" item={property.parcel_size} format="number" emptyState={emptyState} notRequired isReview />}
                  {!property.whole_building &&
                    property.spaces.map((space, i) => (
                      (
                        <div key={uniqueId} >
                          <legend className="secondary-legend">Suite #{i + 1}</legend>
                          <LabeledItem label="Suite" item={space.address_line2} emptyState={emptyState} />
                          {!leaseOrSaleDealType &&
                            <LabeledItem
                              label="Floor"
                              item={space.floor}
                              emptyState={emptyState}
                              icon={isNumericOutOfRange(space.floor, fieldBounds.floor_number) &&
                                <Icon iconName="warning" size="small" />}
                              itemClassName={isNumericOutOfRange(space.floor, fieldBounds.floor_number) ? 'item--warning' : ''}
                            />}
                          {leaseOrSaleDealType &&
                            <RequiredLabeledItem
                              label="Floor"
                              item={space.floor}
                              notRequired
                              isReview
                              fieldId={`property-${property.id || index}-suite-${i}-floor`}
                              section={title}
                              controlType="number"
                              editTarget={{
                                reduxAction: 'updateSpace', fieldPath: 'floor', targetId: property.id, spaceIndex: i,
                              }}
                              bounds={fieldBounds.floor_number}
                              rangeFormat="number"
                            />}
                          {!leaseOrSaleDealType &&
                            <LabeledItem label="Square Feet" item={space.space_total_square_feet} emptyState={emptyState} />}
                          {leaseOrSaleDealType &&
                            <RequiredLabeledItem
                              label="Square Feet"
                              item={space.space_total_square_feet}
                              notRequired={voucher.is_early_invoice}
                              isReview
                              fieldId={`property-${property.id || index}-suite-${i}-square-feet`}
                              section={title}
                              controlType="number"
                              editTarget={{
                                reduxAction: 'updateSpace', fieldPath: 'space_total_square_feet', targetId: property.id, spaceIndex: i,
                              }}
                              bounds={fieldBounds.total_leased_area}
                              rangeFormat="number"
                              rangeUnit="Sq. Ft."
                            />}
                        </div>
                      )
                    ))}
                </div>
              </div>
            </div>
          );
        }) }
      </div>
    </div>

  );
};

PropertyReview.propTypes = {
  fieldBounds: PropTypes.objectOf(PropTypes.shape({
    category: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    minDateReference: PropTypes.shape({
      attributeKey: PropTypes.string,
      operator: PropTypes.string,
    }),
  })),
};

PropertyReview.defaultProps = {
  fieldBounds: {},
};

export default PropertyReview;
